'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import React, { useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import axios from '@/_lib/axiosInstance'
import { IJob } from '@/_types/jobs'
import { pushSimpleEvent } from '@/_utils/analytics/datalayer'
import { hasValidContentBlocks } from '@/app/[locale]/ambulance/[slug]/utils/content.utils'

//Accepted file types
const ACCEPTED = [
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]
const MAX = 5 * 1024 * 1024 // 5MB

type FormValues = {
	firstName: string
	lastName: string
	email: string
	phone: string
	message: string
	files: FileList
	gdpr: boolean
}

export default function AJobForm({ job }: { job: IJob }) {
	const [submitting, setSubmitting] = useState(false)
	const fileInputRef = useRef<HTMLInputElement | null>(null)
	const t = useTranslations('components.job_form')
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm<FormValues>()

	const hasContent = useMemo(() => {
		return job.content && hasValidContentBlocks(job.content)
	}, [job.content])

	const validateFiles = (files?: FileList) => {
		if (!files || files.length === 0) return t('files.required')
		for (const f of Array.from(files)) {
			if (!ACCEPTED.includes(f.type)) return 'Povolené súbory: PDF, DOC, DOCX'
			if (f.size > MAX) return t('files.maxSize')
		}
		return true
	}

	const {
		ref: filesRef,
		onChange: filesOnChange,
		...filesRest
	} = register('files', {
		validate: validateFiles
	})

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		// Call the original react-hook-form onChange first
		await filesOnChange(e)

		// Then handle the visual feedback
		const input = e.target as HTMLInputElement
		if (input.files && input.files.length > 0) {
			input.classList.add('has-files')
		} else {
			input.classList.remove('has-files')
		}
	}

	const onSubmit = async (v: FormValues) => {
		try {
			setSubmitting(true)

			await axios.postForm(
				'/v1/web/contact-form',
				{
					type: 'OPEN_POSITION',
					linkableId: String(job.id),
					firstName: v.firstName,
					lastName: v.lastName,
					email: v.email,
					message: v.message,
					phone: v.phone?.trim() || undefined,
					files: Array.from(v.files), // File[]
					agreedToPersonalDataProtection: v.gdpr ? 1 : 0
				},
				{
					formSerializer: { indexes: false }
				}
			)

			pushSimpleEvent('generate_lead', {
				form_name: 'career',
				form_destination: `pozicia-${job.id}`,
				form_location: typeof window !== 'undefined' ? window.location.pathname : '',
				value: 0,
				currency: 'EUR',
				user_data: {
					email: v.email
				}
			})

			toast.success('Správa bola úspešne odoslaná!', {
				position: 'top-right',
				className: '!bg-medante-pink-secondary',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})

			// Clear file input DOM first
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
				fileInputRef.current.classList.remove('has-files')
			}

			// Then reset form state
			reset({
				firstName: '',
				lastName: '',
				email: '',
				phone: '',
				message: '',
				files: undefined as any,
				gdpr: false
			})
		} catch (e) {
			console.error(e)
			toast.error('Nastala chyba pri odosielaní formulára', {
				position: 'top-right',
				className: '!bg-medante-pink-secondary',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<form
			id='napiste-nam'
			onSubmit={handleSubmit(onSubmit)}
			className={clsx(
				'space-y-4 pdt-100 pdb-100 justify-center',
				hasContent ? 'bg-[#deefff]' : 'bg-[#ffffff]'
			)}
		>
			<div className='md:max-w-[900px] max-w-[90%] mx-auto form-theme-purple'>
				<div className='section_head mgb-80 justify-center'>
					<h2 className='title_side text_uc'>{t('header')}</h2>
					<div className='anim_delay anim_drop1 observe'>
						<h3 className='title_h1 text-center'>{t('title')}</h3>
					</div>
					<p className='text-center'>{t('description')}</p>
				</div>
				<div>
					<div className='grid grid-cols-1 md:grid-cols-2 mgt-10 gap-4'>
						<div className='input_text_cont'>
							<input
								type='text'
								className={`input_text ${errors.firstName ? 'error' : ''}`}
								autoComplete='given-name'
								{...register('firstName', {
									required: t('firstName.required'),
									minLength: { value: 2, message: 'Min. 2 znaky' }
								})}
							/>
							<label className='input_text_placeholder'>{t('firstName.label')}</label>
							{errors.firstName && (
								<p className='text-red-600 text-sm'>{errors.firstName.message}</p>
							)}
						</div>

						<div className='md:col-start-2 input_text_cont'>
							<input
								type='text'
								className={`input_text ${errors.lastName ? 'error' : ''}`}
								{...register('lastName', {
									required: t('lastName.required'),
									minLength: { value: 2, message: 'Min. 2 znaky' }
								})}
							/>
							<label className='input_text_placeholder'>{t('lastName.label')}</label>
							{errors.lastName && (
								<p className='text-red-600 text-sm'>{errors.lastName.message}</p>
							)}
						</div>

						<div className='input_text_cont'>
							<input
								type='email'
								className={`input_text ${errors.email ? 'error' : ''}`}
								{...register('email', {
									required: t('email.required'),
									pattern: {
										value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
										message: 'Neplatný email'
									}
								})}
							/>
							<label className='input_text_placeholder'>{t('email.label')}</label>
							{errors.email && (
								<p className='text-red-600 text-sm'>{errors.email.message}</p>
							)}
						</div>

						<div className='input_text_cont md:col-start-2'>
							<input
								type='tel'
								className={`input_text ${errors.phone ? 'error' : ''}`}
								{...register('phone', {
									required: t('phone.required'),
									minLength: { value: 10, message: 'Neplatný telefón' },
									pattern: { value: /^[+0-9 ()-]+$/, message: 'Neplatný formát' }
								})}
							/>
							<label className='input_text_placeholder'>{t('phone.label')}</label>
							{errors.phone && (
								<p className='text-red-600 text-sm'>{errors.phone.message}</p>
							)}
						</div>

						<div className='input_text_cont md:col-start-1'>
							<textarea
								className={`input_text ${errors.message ? 'error' : ''}`}
								rows={5}
								{...register('message', {
									required: t('message.required'),
									minLength: {
										value: 10,
										message: 'Správa musí mať aspoň 10 znakov'
									}
								})}
							/>
							<label className='input_text_placeholder'>{t('message.label')}</label>
							{errors.message && (
								<p className='text-red-600 text-sm'>{errors.message.message}</p>
							)}
						</div>

						<div className='input_text_cont md:col-start-2'>
							<input
								className={`input_text min-h-[70px] ${errors.files ? 'error' : ''}`}
								type='file'
								accept='.pdf,.doc,.docx'
								multiple
								{...filesRest}
								ref={el => {
									fileInputRef.current = el
									filesRef(el)
								}}
								onChange={handleFileChange}
							/>
							<label className='input_text_placeholder'>{t('files.label')}</label>
							{errors.files && (
								<p className='text-red-600 text-sm'>{errors.files.message}</p>
							)}
						</div>

						<div className='col-start-1 row-start-7 md:row-start-5 md:col-start-1'>
							<label className='input_checkbox_cont '>
								<div className='flex'>
									<input
										type='checkbox'
										className={clsx(
											'input_checkbox',
											!hasContent && '!border-2 !border-medante-purple-dark',
											errors.gdpr ? 'error' : ''
										)}
										{...register('gdpr', {
											required:
												t('gdpr.required') ||
												'Musíš súhlasiť so spracovaním osobných údajov'
										})}
									/>
									<div className='input_checkbox_text'>
										{t('gdpr.label')}
										<Link
											href='/legal/gdpr'
											title='Ochrana osobných údajov'
											className='underline'
											prefetch={false}
										>
											{t('gdpr.link')}
										</Link>
										{errors.gdpr && (
											<p className='text-red-600 text-sm text-[12px]'>
												{String(errors.gdpr.message)}
											</p>
										)}
									</div>
								</div>
							</label>
						</div>
					</div>
				</div>
			</div>

			<div className='flex justify-center pdt-20'>
				<button
					className='btn_basic type6 btn-arrow !cursor-pointer'
					type='submit'
					disabled={submitting}
				>
					<span>{submitting ? t('submit.loading') : t('submit.button')}</span>
					<span>
						<span>
							<svg className='sico'>
								<use href='/assets/layout/icons.svg#s_ar2_r' />
							</svg>
						</span>
						<span>
							<svg className='sico'>
								<use href='/assets/layout/icons.svg#s_ar2_r' />
							</svg>
						</span>
					</span>
				</button>
			</div>
		</form>
	)
}
